import { useEffect, useMemo, useRef, useState } from 'react';
import './VisualIvrLoadingSplash.css';

export interface AvatarLoadingSplashProps {
  headline?: string;
  body?: string;
  hint?: string | null;
  portraitUrl?: string | null;
  /**
   * Tried in order after `portraitUrl` fails to load (Aug 11 2026):
   * lets callers chain captured portrait → manifest portrait →
   * thumbnail, so a 404 from the portrait store degrades gracefully
   * instead of hiding the circle. See buildLoadingPortraitCandidates.
   */
  portraitFallbackUrls?: Array<string | null | undefined>;
  avatarName?: string | null;
  previewText?: string | null;
  contained?: boolean;
}

export function AvatarLoadingSplash({
  headline = 'Please wait...',
  body,
  hint = null,
  portraitUrl,
  portraitFallbackUrls,
  avatarName,
  previewText,
  contained = false
}: AvatarLoadingSplashProps = {}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<'full' | 'compact' | 'mini'>(contained ? 'compact' : 'full');
  const resolvedBody =
    body
    ?? (avatarName ? `${avatarName} is getting ready...` : 'Your virtual assistant is loading');
  const trimmedPreview = typeof previewText === 'string' ? previewText.trim() : '';
  const showPreview = trimmedPreview.length > 0;

  const portraitCandidates = useMemo(() => {
    const list = [portraitUrl, ...(portraitFallbackUrls ?? [])]
      .map((url) => (typeof url === 'string' ? url.trim() : ''))
      .filter((url) => url.length > 0);
    return [...new Set(list)];
  }, [portraitUrl, portraitFallbackUrls]);
  const [portraitIndex, setPortraitIndex] = useState(0);
  const candidatesKey = portraitCandidates.join('|');
  useEffect(() => { setPortraitIndex(0); }, [candidatesKey]);
  const activePortraitUrl = portraitCandidates[portraitIndex] ?? null;

  useEffect(() => {
    if (!contained || !rootRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const classify = (width: number, height: number) => {
      const shortest = Math.min(width, height);
      if (height < 150 || width < 190 || shortest < 150) return 'mini';
      if (height < 280 || width < 300 || shortest < 260) return 'compact';
      return 'full';
    };

    const update = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      setLayout(classify(rect.width, rect.height));
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return update();
      const { width, height } = entry.contentRect;
      setLayout(classify(width, height));
    });

    observer.observe(rootRef.current);
    update();
    return () => observer.disconnect();
  }, [contained]);

  return (
    <div
      ref={rootRef}
      className={`camlin-visual-ivr-splash${contained ? ` camlin-avatar-loading--contained camlin-avatar-loading--${layout}` : ''}`}
      data-camlin-avatar-loading-layout={contained ? layout : 'full'}
      role="status"
      aria-live="polite"
      data-testid="avatar-loading-splash"
    >
      <div className="camlin-visual-ivr-splash__content">
        {activePortraitUrl ? (
          <div className="camlin-visual-ivr-splash__portrait-wrap" aria-hidden="true">
            <img
              key={activePortraitUrl}
              src={activePortraitUrl}
              alt=""
              className="camlin-visual-ivr-splash__portrait"
              onError={() => {
                // Walk the fallback chain; when it's exhausted the
                // component re-renders into the spinner-only branch.
                setPortraitIndex((index) => index + 1);
              }}
            />
            <div className="camlin-visual-ivr-splash__portrait-spinner" />
          </div>
        ) : (
          <div className="camlin-visual-ivr-splash__spinner" aria-hidden="true" />
        )}
        <h2 className="camlin-visual-ivr-splash__headline">{headline}</h2>
        <p className="camlin-visual-ivr-splash__body">{resolvedBody}</p>
        {showPreview ? (
          <p className="camlin-visual-ivr-splash__preview">
            <span className="camlin-visual-ivr-splash__preview-label">
              {avatarName ? avatarName.toUpperCase() : 'ASSISTANT'}
            </span>
            <span className="camlin-visual-ivr-splash__preview-text">
              {trimmedPreview}
            </span>
          </p>
        ) : null}
        {hint ? (
          <p className="camlin-visual-ivr-splash__hint">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

export default AvatarLoadingSplash;
